import dayjs from 'dayjs';
import { createSerializer, parseAsArrayOf, parseAsStringLiteral } from 'nuqs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { DashboardStatistics } from '~/api/statistics/types';

import { invoiceStatusSchema } from '~/api/invoices/schemas';
import { useDashboardStatistics } from '~/api/statistics';
import { Card, Icon, StatusTag, Text, Title } from '~/components';
import { SearchParamKeys } from '~/constants/url';
import { useIntl } from '~/hooks/useIntl';
import { routes } from '~/providers/RouterProvider/router.routes';
import { clsx } from '~/styles/utils';

import {
  amountStyle,
  cardHeaderStyle,
  cardStyle,
  dueDateStyle,
  expiredDueDateStyle,
  invoiceClientStyle,
  invoiceTitleStyle,
  tableCellStyle,
  tableHeaderCellStyle,
  tableRowStyle,
  tableStyle,
  tableWrapperStyle,
  totalAmountsStyle,
  totalAmountStyle,
  totalCardStyle,
  totalLabelStyle,
  viewAllLinkStyle,
} from './OutstandingInvoicesCard.css';

const serializeInvoicesSearchParams = createSerializer({
  [SearchParamKeys.columnFilterStatuses]: parseAsArrayOf(parseAsStringLiteral(invoiceStatusSchema.options)),
});

export const OutstandingInvoicesCard = () => {
  const { data: statistics, isError, isPending } = useDashboardStatistics();

  // Loading and error state are also handled in <StatsSummary />,
  // so it would be redundant to show this twice in the UI.
  if (isPending || isError) return null;

  return <InnerOutstandingInvoicesCard statistics={statistics} />;
};

const InnerOutstandingInvoicesCard = ({ statistics }: { statistics: DashboardStatistics }) => {
  const { t } = useTranslation(['dashboard']);
  const { formatCurrency, formatDate } = useIntl();

  const { expired, invoices, total } = statistics.outstandingInvoices;

  if (total.count === 0) return null;

  return (
    <Card.Root className={cardStyle} variant="white">
      <header className={cardHeaderStyle}>
        <Title as="h2" styleAs="h3">
          {t('dashboard:outstandingInvoices.title')}
        </Title>
      </header>

      <Card.Root className={totalCardStyle} variant="primary-light">
        <Text className={totalLabelStyle}>{t('dashboard:outstandingInvoices.toReceive')}</Text>

        <div className={totalAmountsStyle}>
          <Text className={totalAmountStyle}>{formatCurrency(total.amount)}</Text>

          {expired.count > 0 && (
            <StatusTag
              icon="HourglassBottom"
              label={t('dashboard:outstandingInvoices.expired', { amount: formatCurrency(expired.amount) })}
              status="danger"
            />
          )}
        </div>

        <Text>
          {expired.count > 0
            ? t('dashboard:outstandingInvoices.summaryWithExpired', { count: total.count, expiredCount: expired.count })
            : t('dashboard:outstandingInvoices.summary', { count: total.count })}
        </Text>
      </Card.Root>

      {invoices.length > 0 && (
        <div className={tableWrapperStyle}>
          <table className={tableStyle}>
            <thead>
              <tr className={tableRowStyle}>
                <th className={tableHeaderCellStyle}>{t('dashboard:outstandingInvoices.table.invoice')}</th>
                <th className={tableHeaderCellStyle}>{t('dashboard:outstandingInvoices.table.dueDate')}</th>
                <th className={tableHeaderCellStyle}>{t('dashboard:outstandingInvoices.table.amount')}</th>
              </tr>
            </thead>

            <tbody>
              {invoices.map((invoice, index) => {
                const isExpired = dayjs(invoice.dueDate).isBefore(dayjs(), 'day');

                return (
                  <tr className={tableRowStyle} key={index}>
                    <td className={tableCellStyle}>
                      <Text className={invoiceTitleStyle}>{invoice.title}</Text>
                      <Text className={invoiceClientStyle}>{invoice.clientName}</Text>
                    </td>

                    <td className={clsx(tableCellStyle, dueDateStyle, isExpired && expiredDueDateStyle)}>{formatDate(invoice.dueDate)}</td>

                    <td className={clsx(tableCellStyle, amountStyle)}>{formatCurrency(invoice.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <Link
            className={viewAllLinkStyle}
            to={{
              pathname: routes.invoices,
              search: serializeInvoicesSearchParams({ [SearchParamKeys.columnFilterStatuses]: ['Approved', 'Expired'] }),
            }}
          >
            {t('dashboard:outstandingInvoices.viewAll')}
            <Icon name="ArrowRight" size={20} />
          </Link>
        </div>
      )}
    </Card.Root>
  );
};
